import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Users, Plus, Trash2, UserPlus, Link2, Power, GraduationCap, BarChart3 } from "lucide-react";
import { formatMoney } from "@/lib/money";
import { TeamCsvImport } from "@/components/TeamCsvImport";
import { TeamGradebook } from "@/components/TeamGradebook";

export default function AdminTeams() {
  const [teams, setTeams] = useState(null);
  const [gbTeam, setGbTeam] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [name, setName] = useState("");
  const [price, setPrice] = useState("1000");
  const [seats, setSeats] = useState("10");
  const [busy, setBusy] = useState(false);
  const [memberEmail, setMemberEmail] = useState({});
  const [managerEmail, setManagerEmail] = useState({});

  const load = useCallback(() => api.get("/api/admin/teams").then(({ data }) => setTeams(data.teams)).catch(() => setTeams([])), []);
  useEffect(() => { load(); }, [load]);

  const err = (e, fallback) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || fallback);

  const createTeam = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/api/admin/teams", { name, price_dollars: parseFloat(price), seats: parseInt(seats, 10) });
      toast.success(`Team "${name}" created.`);
      setShowNew(false); setName(""); setPrice("1000"); setSeats("10");
      load();
    } catch (e) { err(e, "Couldn't create team."); }
    finally { setBusy(false); }
  };

  const toggleActive = async (t) => {
    try {
      await api.patch(`/api/admin/teams/${t.id}/active`, { active: !t.manual_active });
      toast.success(`${t.name} ${!t.manual_active ? "activated" : "deactivated"}.`);
      load();
    } catch (e) { err(e, "Couldn't update team."); }
  };

  const addMember = async (t) => {
    const email = (memberEmail[t.id] || "").trim();
    if (!email) return;
    try {
      await api.post(`/api/admin/teams/${t.id}/members`, { email });
      toast.success(`Added ${email}.`);
      setMemberEmail((m) => ({ ...m, [t.id]: "" }));
      load();
    } catch (e) { err(e, "Couldn't add member."); }
  };

  const removeMember = async (t, uid) => {
    try {
      await api.delete(`/api/admin/teams/${t.id}/members/${uid}`);
      load();
    } catch (e) { err(e, "Couldn't remove member."); }
  };

  const addManager = async (t) => {
    const email = (managerEmail[t.id] || "").trim();
    if (!email) return;
    try {
      await api.post(`/api/admin/teams/${t.id}/managers`, { email });
      toast.success(`${email} can now manage this team.`);
      setManagerEmail((m) => ({ ...m, [t.id]: "" }));
      load();
    } catch (e) { err(e, "Couldn't assign instructor."); }
  };

  const removeManager = async (t, uid) => {
    try {
      await api.delete(`/api/admin/teams/${t.id}/managers/${uid}`);
      load();
    } catch (e) { err(e, "Couldn't remove instructor."); }
  };

  const delTeam = async (t) => {
    try { await api.delete(`/api/admin/teams/${t.id}`); toast.success(`Deleted ${t.name}.`); load(); }
    catch (e) { err(e, "Couldn't delete team."); }
  };

  const paymentLink = async (t) => {
    try {
      const { data } = await api.post(`/api/admin/teams/${t.id}/payment-link`, { origin_url: window.location.origin });
      await navigator.clipboard.writeText(data.url).catch(() => {});
      toast.success("Payment link copied to clipboard — send it to the team manager.");
    } catch (e) { err(e, "Couldn't create payment link."); }
  };

  return (
    <div data-testid="admin-teams-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#5ec0ff] font-semibold mb-1">
            <Users className="w-3.5 h-3.5" /> Teams
          </div>
          <h1 className="text-2xl font-bold text-white">Team plans</h1>
          <p className="text-sm text-slate-400 mt-1">Create clinic/college teams with custom pricing and assign members.</p>
        </div>
        <Button onClick={() => setShowNew(true)} data-testid="new-team-btn" className="bg-[#0047AB] hover:bg-[#003380] text-white">
          <Plus className="w-4 h-4 mr-1.5" /> New team
        </Button>
      </div>

      <div className="mt-6 space-y-4">
        {teams === null ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm py-16 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : teams.length === 0 ? (
          <div className="text-center text-slate-500 py-16 text-sm rounded-xl border border-slate-800 bg-[#0e1726]">No teams yet.</div>
        ) : (
          teams.map((t) => (
            <div key={t.id} className="rounded-xl border border-slate-800 bg-[#0e1726] p-5" data-testid={`team-row-${t.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold text-white">{t.name}</h3>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${t.active ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-slate-600 bg-slate-700/40 text-slate-400"}`}>
                      {t.active ? "ACTIVE" : "INACTIVE"}
                    </span>
                  </div>
                  <p className="text-sm text-slate-400 mt-0.5">
                    {formatMoney(t.price_cents, t.currency)} / {t.interval} · {t.member_count}/{t.seats} seats
                  </p>
                </div>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" onClick={() => toggleActive(t)} data-testid={`team-toggle-${t.id}`}
                    className={`text-xs ${t.manual_active ? "border-amber-600/50 text-amber-300" : "border-emerald-600/50 text-emerald-300"} bg-transparent hover:bg-slate-800`}>
                    <Power className="w-3.5 h-3.5 mr-1" /> {t.manual_active ? "Deactivate" : "Activate (comp)"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => paymentLink(t)} data-testid={`team-paylink-${t.id}`}
                    className="text-xs bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800">
                    <Link2 className="w-3.5 h-3.5 mr-1" /> Payment link
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setGbTeam(t)} data-testid={`team-gradebook-${t.id}`}
                    className="text-xs bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800">
                    <BarChart3 className="w-3.5 h-3.5 mr-1" /> Gradebook
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => delTeam(t)} data-testid={`team-delete-${t.id}`}
                    className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 px-2">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>

              {/* instructors / managers */}
              <div className="mt-4 border-t border-slate-800 pt-3">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-2">
                  <GraduationCap className="w-3.5 h-3.5" /> Instructors (can manage roster)
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {(t.managers || []).length === 0 ? (
                    <span className="text-xs text-slate-500">No instructor assigned yet.</span>
                  ) : t.managers.map((m) => (
                    <span key={m.id} className="inline-flex items-center gap-1.5 bg-[#0047AB]/20 text-sky-200 text-xs rounded-full pl-3 pr-1.5 py-1 border border-[#0047AB]/40" data-testid={`team-manager-${m.id}`}>
                      {m.email}
                      <button onClick={() => removeManager(t, m.id)} className="text-slate-400 hover:text-rose-400" data-testid={`team-manager-remove-${m.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input value={managerEmail[t.id] || ""} onChange={(e) => setManagerEmail((mm) => ({ ...mm, [t.id]: e.target.value }))}
                    placeholder="instructor@email.com" data-testid={`team-manager-input-${t.id}`}
                    className="bg-[#0b1220] border-slate-700 text-white h-9 text-sm max-w-xs" />
                  <Button size="sm" onClick={() => addManager(t)} data-testid={`team-add-manager-${t.id}`} className="bg-slate-700 hover:bg-slate-600 text-white">
                    <GraduationCap className="w-3.5 h-3.5 mr-1" /> Assign
                  </Button>
                </div>
              </div>

              {/* members */}
              <div className="mt-4 border-t border-slate-800 pt-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
                    Members · {t.member_count}/{t.seats} seats
                  </div>
                  <TeamCsvImport
                    endpoint={`/api/admin/teams/${t.id}/members/bulk`}
                    teamName={t.name}
                    seatsLeft={Math.max(0, (t.seats || 0) - t.member_count)}
                    testid={`team-${t.id}-csv`}
                    onImported={() => load()}
                  />
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {t.members.length === 0 ? (
                    <span className="text-xs text-slate-500">No members assigned yet.</span>
                  ) : t.members.map((m) => (
                    <span key={m.id} className="inline-flex items-center gap-1.5 bg-slate-800 text-slate-200 text-xs rounded-full pl-3 pr-1.5 py-1" data-testid={`team-member-${m.id}`}>
                      {m.email}
                      {(t.manager_ids || []).includes(m.id)
                        ? <span className="text-[9px] uppercase tracking-wide text-sky-300/80 font-semibold pr-1">instructor</span>
                        : (
                        <button onClick={() => removeMember(t, m.id)} className="text-slate-400 hover:text-rose-400" data-testid={`team-member-remove-${m.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input value={memberEmail[t.id] || ""} onChange={(e) => setMemberEmail((mm) => ({ ...mm, [t.id]: e.target.value }))}
                    placeholder="member@email.com" data-testid={`team-member-input-${t.id}`}
                    className="bg-[#0b1220] border-slate-700 text-white h-9 text-sm max-w-xs" />
                  <Button size="sm" onClick={() => addMember(t)} data-testid={`team-add-member-${t.id}`} className="bg-slate-700 hover:bg-slate-600 text-white">
                    <UserPlus className="w-3.5 h-3.5 mr-1" /> Add
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <Dialog open={!!gbTeam} onOpenChange={(o) => !o && setGbTeam(null)}>
        <DialogContent data-testid="gradebook-dialog" className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{gbTeam?.name} · Gradebook</DialogTitle>
            <DialogDescription>Best score per simulator, competency and completion for each learner.</DialogDescription>
          </DialogHeader>
          {gbTeam && <TeamGradebook teamId={gbTeam.id} teamName={gbTeam.name} />}
        </DialogContent>
      </Dialog>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent data-testid="new-team-dialog">
          <DialogHeader>
            <DialogTitle>Create a team</DialogTitle>
            <DialogDescription>Set a custom annual price — each team can be charged differently.</DialogDescription>
          </DialogHeader>
          <form onSubmit={createTeam} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Team name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="team-name-input" required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Annual price (AUD)</Label>
                <Input type="number" step="0.01" min="1" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="team-price-input" required />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Seats</Label>
                <Input type="number" min="1" value={seats} onChange={(e) => setSeats(e.target.value)} data-testid="team-seats-input" required />
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={busy} data-testid="create-team-submit" className="bg-[#0047AB] hover:bg-[#003380] text-white">
                {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Create team
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
